import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useState,
} from "react";
import { type ToastProps } from "../components/ui/Toast";
import ToastContainer from "../components/ui/ToastContainer";

interface ToastContextType {
	showToast: (toast: Omit<ToastProps, "id">) => string;
	removeToast: (id?: string) => void;
	success: (message: string, title?: string) => string;
	error: (message: string, title?: string) => string;
	warning: (message: string, title?: string) => string;
	info: (message: string, title?: string) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within ToastProvider");
	}
	return context;
};

interface ToastProviderProps {
	children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
	const [toasts, setToasts] = useState<ToastProps[]>([]);

	const showToast = useCallback((toast: Omit<ToastProps, "id">) => {
		const id = Math.random().toString(36).substring(7);
		const newToast: ToastProps = { ...toast, id };
		setToasts((prev) => [...prev, newToast]);
		return id;
	}, []);

	const removeToast = useCallback((id?: string) => {
		if (id) {
			setToasts((prev) => prev.filter((toast) => toast.id !== id));
		}
	}, []);

	const success = useCallback(
		(message: string, title?: string) => {
			return showToast({ message, title, type: "success" });
		},
		[showToast],
	);

	const error = useCallback(
		(message: string, title?: string) => {
			return showToast({ message, title, type: "error" });
		},
		[showToast],
	);

	const warning = useCallback(
		(message: string, title?: string) => {
			return showToast({ message, title, type: "warning" });
		},
		[showToast],
	);

	const info = useCallback(
		(message: string, title?: string) => {
			return showToast({ message, title, type: "info" });
		},
		[showToast],
	);

	return (
		<ToastContext.Provider
			value={{
				showToast,
				removeToast,
				success,
				error,
				warning,
				info,
			}}
		>
			{children}
			<ToastContainer toasts={toasts} onRemoveToast={removeToast} />
		</ToastContext.Provider>
	);
};
